// Attendance and shift corrections (architecture sprint-13 §9, §11, §14.2). The attendance query is live
// (30 s while visible) so "On shift now" stays current; corrections invalidate ['attendance', projectId] and
// ['shifts']. Errors are shown by the dialogs (meta.silent).
import { keepPreviousData, useMutation, useQuery, useQueryClient, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query';
import type { CheckinSlotDto } from '@shared/alarm-rules';
import type { LocalDate } from '@shared/calendar';
import { apiRequest } from '@/lib/api';
import { LIVE_QUERY_OPTIONS } from '@/lib/live-query';
import type { AttendanceDto, CorrectionWarnings, NoteShiftDto, ShiftDto, ShiftLocationValue, ShiftRevisionDto, WallClockBody } from '@/types/shifts';

function base(projectId: string): string {
  return `/api/oversight/projects/${projectId}`;
}

export function useAttendance(projectId: string | null, from: LocalDate, to: LocalDate): UseQueryResult<AttendanceDto> {
  return useQuery({
    queryKey: ['attendance', projectId, from, to],
    queryFn: () => apiRequest<AttendanceDto>(`${base(projectId ?? '')}/attendance?from=${from}&to=${to}`),
    enabled: projectId !== null,
    placeholderData: keepPreviousData,
    ...LIVE_QUERY_OPTIONS,
  });
}

/** Sprint 14: `checkins` (oldest first, cancelled included) is absent on an older server. */
export interface ShiftDetail {
  shift: ShiftDto;
  revisions: ShiftRevisionDto[];
  checkins?: CheckinSlotDto[];
}

export function useShiftDetail(projectId: string, shiftId: string | null): UseQueryResult<ShiftDetail> {
  return useQuery({
    queryKey: ['attendance', projectId, 'shift', shiftId],
    queryFn: () => apiRequest<ShiftDetail>(`${base(projectId)}/shifts/${shiftId ?? ''}`),
    enabled: shiftId !== null,
  });
}

function useInvalidateAttendance(projectId: string): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['attendance', projectId] }),
      queryClient.invalidateQueries({ queryKey: ['shifts'] }),
    ]);
  };
}

export interface CorrectBody {
  version: number;
  start?: WallClockBody;
  end?: WallClockBody;
  location?: ShiftLocationValue | null;
  reason: string;
}

export interface AddShiftBody {
  userId: string;
  start: WallClockBody;
  end: WallClockBody;
  location?: ShiftLocationValue | null;
  reason: string;
}

export function useCorrectShift(projectId: string): UseMutationResult<{ shift: ShiftDto; warnings: CorrectionWarnings }, Error, { shiftId: string; body: CorrectBody }> {
  const invalidate = useInvalidateAttendance(projectId);
  return useMutation({
    mutationFn: ({ shiftId, body }) =>
      apiRequest<{ shift: ShiftDto; warnings: CorrectionWarnings }>(`${base(projectId)}/shifts/${shiftId}`, { method: 'PATCH', body: JSON.stringify(body) }),
    meta: { silent: true },
    onSuccess: invalidate,
  });
}

export function useAddShift(projectId: string): UseMutationResult<{ shift: ShiftDto; warnings: CorrectionWarnings }, Error, AddShiftBody> {
  const invalidate = useInvalidateAttendance(projectId);
  return useMutation({
    mutationFn: (body) => apiRequest<{ shift: ShiftDto; warnings: CorrectionWarnings }>(`${base(projectId)}/shifts`, { method: 'POST', body: JSON.stringify(body) }),
    meta: { silent: true },
    onSuccess: invalidate,
  });
}

export function useVoidShift(projectId: string): UseMutationResult<{ shift: ShiftDto }, Error, { shiftId: string; version: number; reason: string }> {
  const invalidate = useInvalidateAttendance(projectId);
  return useMutation({
    mutationFn: ({ shiftId, version, reason }) =>
      apiRequest<{ shift: ShiftDto }>(`${base(projectId)}/shifts/${shiftId}/void`, { method: 'POST', body: JSON.stringify({ version, reason }) }),
    meta: { silent: true },
    onSuccess: invalidate,
  });
}

/** §13: the member's shifts on that day, for the note Shift select. Silent: the field just stays "None". */
export function useNoteShiftOptions(projectId: string, userId: string, date: LocalDate, enabled: boolean): UseQueryResult<NoteShiftDto[]> {
  return useQuery({
    queryKey: ['oversight', projectId, 'notes', 'shift-options', userId, date],
    queryFn: async () =>
      (await apiRequest<{ items: NoteShiftDto[] }>(`${base(projectId)}/notes/shift-options?userId=${userId}&date=${date}`)).items,
    enabled: enabled && userId !== '' && date !== '',
    meta: { silent: true },
  });
}
